import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import {
  DIAMOND_TYPES,
  STONE_SHAPES,
  STONE_TYPES,
  STONE_OWNERSHIPS,
  STONE_POSITION_CATEGORIES,
  SETTING_TYPES,
} from '@/utils/constants';

// Read either the canonical CAD/backend field name or the legacy Task-form name
// so existing rows keep displaying correctly while new edits write the canonical key.
const sizeOf = (d) => d.size_mm ?? d.stone_size_mm ?? '';
const weightOf = (d) => d.weight_per_stone ?? d.stone_weight_ct ?? '';
const qtyOf = (d) => d.quantity ?? d.pieces ?? '';

export function TaskDiamondsTab({ formData, onChange, canEdit, emptyDiamond, emptyStone }) {
  const addDiamond = () => onChange('diamonds', [...formData.diamonds, { ...emptyDiamond }]);
  const removeDiamond = (i) => onChange('diamonds', formData.diamonds.filter((_, idx) => idx !== i));
  const updateDiamond = (i, field, value) =>
    onChange('diamonds', formData.diamonds.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)));

  const addOtherStone = () => onChange('other_stones', [...formData.other_stones, { ...emptyStone }]);
  const removeOtherStone = (i) => onChange('other_stones', formData.other_stones.filter((_, idx) => idx !== i));
  const updateOtherStone = (i, field, value) =>
    onChange('other_stones', formData.other_stones.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));

  const totalQty = formData.diamonds.reduce((sum, d) => sum + (parseInt(qtyOf(d)) || 0), 0);
  const totalWt = formData.diamonds.reduce(
    (sum, d) => sum + (parseFloat(weightOf(d)) || 0) * (parseInt(qtyOf(d)) || 0),
    0,
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Diamond Summary</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <Label htmlFor="diamond_type">Type</Label>
            <select
              id="diamond_type"
              value={formData.diamond_type}
              onChange={(e) => onChange('diamond_type', e.target.value)}
              disabled={!canEdit}
              className="mt-2 w-full h-10 px-3 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="">Select</option>
              {DIAMOND_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="total_diamond_pieces">Total Pieces</Label>
            <Input
              id="total_diamond_pieces"
              type="number"
              value={formData.total_diamond_pieces}
              onChange={(e) => onChange('total_diamond_pieces', e.target.value)}
              disabled={!canEdit}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="total_diamond_weight_ct">Total Weight (ct)</Label>
            <Input
              id="total_diamond_weight_ct"
              type="number"
              step="0.01"
              value={formData.total_diamond_weight_ct}
              onChange={(e) => onChange('total_diamond_weight_ct', e.target.value)}
              disabled={!canEdit}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="diamond_quality_code">Quality</Label>
            <Input
              id="diamond_quality_code"
              value={formData.diamond_quality_code}
              onChange={(e) => onChange('diamond_quality_code', e.target.value)}
              placeholder="FG-VS"
              disabled={!canEdit}
              className="mt-2"
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">Diamond Breakdown</h3>
            {formData.diamonds.length > 0 && (
              <p className="text-xs text-slate-500 mt-0.5">
                Totals: {totalQty} pcs · {totalWt.toFixed(2)} ct
              </p>
            )}
          </div>
          {canEdit && (
            <Button type="button" onClick={addDiamond} size="sm" variant="outline">
              <Plus size={16} className="mr-2" />Add Diamond
            </Button>
          )}
        </div>
        {formData.diamonds.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg text-slate-500">No diamond entries</div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b">
                <tr>
                  {['Type', 'Ownership', 'Cut', 'Position', 'Quality', 'Setting', 'Size (mm)', 'Wt/pc (ct)', 'Qty', 'Total', ''].map((h) => (
                    <th key={h} className="text-left py-2 px-2 font-medium text-slate-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formData.diamonds.map((d, idx) => {
                  const sizeVal = sizeOf(d);
                  const wtVal = weightOf(d);
                  const qtyVal = qtyOf(d);
                  const total = (parseFloat(wtVal) || 0) * (parseInt(qtyVal) || 0);
                  const stoneTypeIsKnown = STONE_TYPES.includes(d.stone_category);
                  const ownershipIsKnown = STONE_OWNERSHIPS.includes(d.ownership);
                  const shapeIsKnown = STONE_SHAPES.includes(d.stone_shape);
                  return (
                    <tr key={d.id || `diamond-${idx}`} className="border-b border-slate-100 last:border-0">
                      <td className="p-1">
                        {stoneTypeIsKnown || !d.stone_category ? (
                          <select
                            value={d.stone_category || 'Natural'}
                            onChange={(e) => {
                              if (e.target.value === '__other') updateDiamond(idx, 'stone_category', '');
                              else updateDiamond(idx, 'stone_category', e.target.value);
                            }}
                            disabled={!canEdit}
                            className="w-[90px] h-7 px-1 text-xs border rounded bg-white"
                          >
                            {STONE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            <option value="__other">Other...</option>
                          </select>
                        ) : (
                          <Input
                            value={d.stone_category || ''}
                            onChange={(e) => updateDiamond(idx, 'stone_category', e.target.value)}
                            disabled={!canEdit}
                            className="w-[90px] h-7 px-1 text-xs"
                            placeholder="Type..."
                          />
                        )}
                      </td>
                      <td className="p-1">
                        {ownershipIsKnown || !d.ownership ? (
                          <select
                            value={d.ownership || 'KS Goods'}
                            onChange={(e) => {
                              if (e.target.value === '__other') updateDiamond(idx, 'ownership', '');
                              else updateDiamond(idx, 'ownership', e.target.value);
                            }}
                            disabled={!canEdit}
                            className="w-[90px] h-7 px-1 text-xs border rounded bg-white"
                          >
                            {STONE_OWNERSHIPS.map((o) => <option key={o} value={o}>{o}</option>)}
                            <option value="__other">Other...</option>
                          </select>
                        ) : (
                          <Input
                            value={d.ownership || ''}
                            onChange={(e) => updateDiamond(idx, 'ownership', e.target.value)}
                            disabled={!canEdit}
                            className="w-[90px] h-7 px-1 text-xs"
                            placeholder="Owner..."
                          />
                        )}
                      </td>
                      <td className="p-1">
                        {shapeIsKnown || !d.stone_shape ? (
                          <select
                            value={d.stone_shape || 'Round'}
                            onChange={(e) => {
                              if (e.target.value === '__other') updateDiamond(idx, 'stone_shape', '');
                              else updateDiamond(idx, 'stone_shape', e.target.value);
                            }}
                            disabled={!canEdit}
                            className="w-[90px] h-7 px-1 text-xs border rounded bg-white"
                          >
                            {STONE_SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
                            <option value="__other">Other...</option>
                          </select>
                        ) : (
                          <Input
                            value={d.stone_shape || ''}
                            onChange={(e) => updateDiamond(idx, 'stone_shape', e.target.value)}
                            disabled={!canEdit}
                            className="w-[90px] h-7 px-1 text-xs"
                            placeholder="Cut..."
                          />
                        )}
                      </td>
                      <td className="p-1">
                        <select
                          value={d.category || 'Accent Stone'}
                          onChange={(e) => updateDiamond(idx, 'category', e.target.value)}
                          disabled={!canEdit}
                          className="w-[90px] h-7 px-1 text-xs border rounded bg-white"
                        >
                          {STONE_POSITION_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <Input
                          value={d.stone_quality || ''}
                          onChange={(e) => updateDiamond(idx, 'stone_quality', e.target.value)}
                          disabled={!canEdit}
                          className="w-[70px] h-7 px-1 text-xs"
                          placeholder="VS1"
                        />
                      </td>
                      <td className="p-1">
                        <select
                          value={d.setting_type || ''}
                          onChange={(e) => updateDiamond(idx, 'setting_type', e.target.value)}
                          disabled={!canEdit}
                          className="w-[90px] h-7 px-1 text-xs border rounded bg-white"
                        >
                          <option value="">—</option>
                          {SETTING_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <Input
                          value={sizeVal}
                          onChange={(e) => updateDiamond(idx, 'size_mm', e.target.value)}
                          disabled={!canEdit}
                          className="w-[70px] h-7 px-1 text-xs"
                          placeholder="mm"
                        />
                      </td>
                      <td className="p-1">
                        <Input
                          type="number"
                          step="0.001"
                          value={wtVal}
                          onChange={(e) => updateDiamond(idx, 'weight_per_stone', e.target.value)}
                          disabled={!canEdit}
                          className="w-[80px] h-7 px-1 text-xs"
                          placeholder="ct"
                        />
                      </td>
                      <td className="p-1">
                        <Input
                          type="number"
                          value={qtyVal}
                          onChange={(e) => updateDiamond(idx, 'quantity', e.target.value)}
                          disabled={!canEdit}
                          className="w-[60px] h-7 px-1 text-xs"
                          placeholder="Qty"
                        />
                      </td>
                      <td className="p-1 text-slate-500 text-center">{total ? total.toFixed(3) : '—'}</td>
                      <td className="p-1">
                        {canEdit && (
                          <Button
                            type="button"
                            onClick={() => removeDiamond(idx)}
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-rose-600"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between mb-4">
          <h3 className="text-lg font-semibold">Other Stones</h3>
          {canEdit && (
            <Button type="button" onClick={addOtherStone} size="sm" variant="outline">
              <Plus size={16} className="mr-2" />Add Stone
            </Button>
          )}
        </div>
        {formData.other_stones.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg text-slate-500">No other stones</div>
        ) : (
          <div className="space-y-4">
            {formData.other_stones.map((stone, idx) => (
              <div key={stone.id || `stone-${idx}`} className="p-4 border rounded-lg bg-slate-50">
                <div className="flex justify-between mb-3">
                  <h4 className="font-semibold">Stone #{idx + 1}</h4>
                  {canEdit && (
                    <Button
                      type="button"
                      onClick={() => removeOtherStone(idx)}
                      size="sm"
                      variant="ghost"
                      className="text-rose-600"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={stone.stone_name}
                      onChange={(e) => updateOtherStone(idx, 'stone_name', e.target.value)}
                      placeholder="Ruby, Emerald"
                      disabled={!canEdit}
                      className="mt-1 h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Shape</Label>
                    <Input
                      value={stone.shape}
                      onChange={(e) => updateOtherStone(idx, 'shape', e.target.value)}
                      disabled={!canEdit}
                      className="mt-1 h-9 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Size (mm)</Label>
                    <Input
                      value={stone.size_mm}
                      onChange={(e) => updateOtherStone(idx, 'size_mm', e.target.value)}
                      disabled={!canEdit}
                      className="mt-1 h-9 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
